import { RefObject, useCallback, useEffect, useState } from 'react';

interface IUseElementFullyScrolled {
  isFullyScrolled: boolean;
}

interface IUseElementFullyScrolledOptionsProp {
  scrollOffset?: number;
  recheckTrigger?: unknown;
}

export const useElementFullyScrolled = <T extends HTMLElement>(
  ref: RefObject<T>,
  options?: IUseElementFullyScrolledOptionsProp
): IUseElementFullyScrolled => {
  const [isFullyScrolled, setIsFullyScrolled] = useState(false);
  const scrollOffset = options?.scrollOffset ?? 10;

  const checkScroll = useCallback(() => {
    if (!ref.current) {
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = ref.current;
    const atBottom = scrollTop + clientHeight >= scrollHeight - scrollOffset;
    const noScrollNeeded = scrollHeight <= clientHeight + scrollOffset;

    setIsFullyScrolled(atBottom || noScrollNeeded);
  }, [ref, scrollOffset]);

  useEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    checkScroll();
    element.addEventListener('scroll', checkScroll, { passive: true });

    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener('scroll', checkScroll);
      resizeObserver.disconnect();
    };
  }, [checkScroll, options?.recheckTrigger]);

  return { isFullyScrolled };
};
